import { create } from "zustand";
import { Mail, MailFolder, MailThread } from "./types";

interface MailState {
  selectedFolder: MailFolder;
  setSelectedFolder: (folder: MailFolder) => void;
  selectedMail: Mail | null;
  setSelectedMail: (mail: Mail | null) => void;
  selectedThread: MailThread | null;
  setSelectedThread: (thread: MailThread | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  // Multi-select state
  selectedEmailIds: Set<string>;
  toggleEmailSelection: (emailId: string) => void;
  selectAllEmails: (emailIds: string[]) => void;
  clearSelection: () => void;
  isMultiSelectMode: boolean;
  setMultiSelectMode: (mode: boolean) => void;
}

export const useMail = create<MailState>((set) => ({
  selectedFolder: "inbox",
  setSelectedFolder: (folder) => set({ selectedFolder: folder, selectedMail: null, selectedThread: null, selectedEmailIds: new Set(), isMultiSelectMode: false }),
  selectedMail: null,
  setSelectedMail: (mail) => set({ selectedMail: mail }),
  selectedThread: null,
  setSelectedThread: (thread) => set({ selectedThread: thread, selectedMail: thread?.latestMail || null }),
  searchQuery: "",
  setSearchQuery: (query) => set({ searchQuery: query }),
  // Multi-select
  selectedEmailIds: new Set(),
  toggleEmailSelection: (emailId) => set((state) => {
    const newSet = new Set(state.selectedEmailIds);
    if (newSet.has(emailId)) {
      newSet.delete(emailId);
    } else {
      newSet.add(emailId);
    }
    return { selectedEmailIds: newSet };
  }),
  selectAllEmails: (emailIds) => set({ selectedEmailIds: new Set(emailIds) }),
  clearSelection: () => set({ selectedEmailIds: new Set(), isMultiSelectMode: false }),
  isMultiSelectMode: false,
  setMultiSelectMode: (mode) => set({ isMultiSelectMode: mode, selectedEmailIds: mode ? new Set() : new Set() }),
}));

// Helper to group emails into threads based on subject and in_reply_to
export function groupEmailsIntoThreads(emails: Mail[]): MailThread[] {
  const threadMap = new Map<string, Mail[]>();
  
  // First pass: group by normalized subject or in_reply_to chain
  emails.forEach((email) => {
    // Normalize subject by removing Re:, Fwd:, etc.
    const normalizedSubject = (email.subject || "")
      .replace(/^(Re:|Fwd:|FW:|RE:)\s*/gi, "")
      .trim()
      .toLowerCase();
    
    // Use message_id chain or subject for grouping
    const threadKey = normalizedSubject || email.id;
    
    if (!threadMap.has(threadKey)) {
      threadMap.set(threadKey, []);
    }
    threadMap.get(threadKey)!.push(email);
  });
  
  // Convert to threads
  const threads: MailThread[] = [];
  threadMap.forEach((mails, key) => {
    // Sort mails within thread by date (newest first)
    mails.sort((a, b) => 
      new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
    );
    
    const latestMail = mails[0];
    const unreadCount = mails.filter((m) => !m.is_read).length;
    
    threads.push({
      id: key,
      subject: latestMail.subject || "(No Subject)",
      mails,
      latestMail,
      unreadCount,
      totalCount: mails.length,
    });
  });
  
  // Sort threads by latest mail date
  threads.sort((a, b) => 
    new Date(b.latestMail.received_at).getTime() - 
    new Date(a.latestMail.received_at).getTime()
  );
  
  return threads;
}
